/**
 * Structural CSS for every block type, ported 1:1 from the BCE design
 * mockup's theme. Colors come exclusively from CSS variables that the
 * runtime injects from `document.theme.colors` — so blocks restyle with the
 * theme. The theme's own `baseCss` carries only fonts, body and `.page`
 * fundamentals.
 */
export const BLOCKS_CSS = `
/* ---- type scale ---- */
.eyebrow{font-size:8.5pt;font-weight:800;letter-spacing:.16em;text-transform:uppercase;color:var(--royal);margin:0 0 6px}
h1,h2,h3{margin:0;letter-spacing:-.02em;font-weight:800;color:var(--royal)}
h1{font-size:34pt;line-height:1.02;font-weight:900}
h2{font-size:19pt;margin-bottom:10px}
h3{font-size:12.5pt;margin-bottom:5px}
p{margin:0 0 11px}
strong,b{color:var(--navy);font-weight:700}
a{color:var(--royal)}
.txt-sm{font-size:9.5pt}
.txt-fine{font-size:8.5pt;color:var(--blue)}

/* ---- rule + divider ---- */
.rule{width:74px;height:5px;background:var(--yellow);margin:20px 0 26px;border-radius:3px}
.divider{border:0;border-top:1px solid var(--line);margin:10px 0}

/* ---- cover decor orbs ----
   The decor block is NOT a positioned ancestor: orbs anchor to the .page
   (the nearest positioned element) and bleed off its edges, clipped by the
   page's overflow — matching the design mockup. Content stacks above via
   its own positioned layer. */
.decor{display:flex;flex-direction:column;flex:1}
.decor>.decor-content{position:relative;display:flex;flex-direction:column;flex:1}
.orb{position:absolute;border-radius:50%;pointer-events:none}
.orb--a{width:430px;height:430px;right:-150px;top:-120px;background:var(--royal);opacity:.5}
.orb--b{width:250px;height:250px;right:30px;top:200px;border:2px solid var(--sky);opacity:.4;background:none}
.orb--c{width:96px;height:96px;right:-24px;bottom:150px;background:var(--yellow);opacity:.95}

/* ---- section header ---- */
.sec-head{border-bottom:2px solid var(--line);padding-bottom:11px;margin-bottom:20px}

/* ---- grid + card (symmetrical cells; brand-tinted boxes) ---- */
.grid{display:grid;grid-auto-rows:1fr;gap:16px;margin:6px 0}
.card{padding:18px 20px;border-radius:12px;border:1px solid transparent}
/* Even, professional spacing between a card's children (icon, title, body) by
   default — overriding the blocks' own margins so the rhythm is uniform. An
   explicit mt/mb style on a child still wins (inline style beats this rule). */
.card>*{margin:0 0 10px}
.card>*:last-child{margin-bottom:0}
.card--outline{border-color:var(--line)}
.card--mist{background:var(--mist)}
.card--sky{background:color-mix(in srgb, var(--sky) 15%, #fff);border-color:color-mix(in srgb, var(--sky) 40%, #fff)}
.card--royal{background:color-mix(in srgb, var(--royal) 8%, #fff);border-color:color-mix(in srgb, var(--royal) 28%, #fff)}
.card--yellow{background:color-mix(in srgb, var(--yellow) 22%, #fff);border-color:color-mix(in srgb, var(--yellow) 55%, #fff)}
.card--navy{background:var(--navy)}
.card--navy h1,.card--navy h2,.card--navy h3,.card--navy .eyebrow{color:#fff}
.card--navy p,.card--navy{color:rgba(255,255,255,.86)}

/* ---- icon badge (round brand chip around a lucide icon) ---- */
.icon-badge{display:inline-flex;align-items:center;justify-content:center;width:48px;height:48px;border-radius:50%;line-height:0;flex:none}
.icon-badge--yellow{background:var(--yellow);color:var(--navy)}
.icon-badge--navy{background:var(--navy);color:#fff}
.icon-badge--royal{background:var(--royal);color:#fff}
.icon-badge--sky{background:color-mix(in srgb, var(--sky) 30%, #fff);color:var(--navy)}
.icon-badge--mist{background:var(--mist);color:var(--navy)}

/* ---- stat bar (program spec strip) ---- */
.specs{display:flex;gap:0;border:1px solid var(--line);border-radius:10px;overflow:hidden;margin:16px 0 20px}
.spec{flex:1;padding:12px 8px;text-align:center;border-right:1px solid var(--line)}
.spec:last-child{border-right:0}
.spec .v{font-size:11.5pt;font-weight:800;color:var(--navy);line-height:1.2}
.spec .l{font-size:7.5pt;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--blue);margin-top:3px}

/* ---- data table ---- */
.invest{border:1px solid var(--line);border-radius:12px;overflow:hidden;margin:6px 0 0}
.invest table{width:100%;border-collapse:collapse}
.invest th{background:var(--navy);color:#fff;text-align:left;font-size:8pt;font-weight:800;letter-spacing:.12em;text-transform:uppercase;padding:11px 16px}
.invest th.num{text-align:right}
.invest td{padding:12px 16px;border-bottom:1px solid var(--line);vertical-align:top;font-size:10pt}
.invest td.num{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums;font-weight:600;color:var(--navy)}
.invest tr:last-child td{border-bottom:0}
.invest .sub td{background:var(--mist);font-weight:700;color:var(--navy)}
.invest .disc td.num{color:var(--yellow-dark);font-weight:700}
.invest .note{font-size:8.5pt;color:var(--blue);font-weight:500;display:block;margin-top:3px;line-height:1.45}
.invest .total td{background:var(--navy);color:#fff;font-size:12.5pt;font-weight:800;padding:15px 16px}
.invest .total td.num{color:var(--yellow);font-size:16pt}

/* ---- callout ---- */
.savings{margin-top:16px;background:var(--mist);border-left:5px solid var(--yellow);border-radius:0 10px 10px 0;padding:15px 18px}
.savings .big{font-size:15pt;font-weight:800;color:var(--navy);letter-spacing:-.01em}
.savings p{margin:5px 0 0;font-size:9.5pt}
.mission{background:var(--mist);border-radius:12px;padding:18px 20px;margin-bottom:20px}
.mission p{margin:0;font-size:10pt}

/* ---- checklist ---- */
.check-group{margin-bottom:16px;break-inside:avoid}
.check-group > h3{color:var(--navy);font-size:11.5pt;margin-bottom:7px}
.check{display:flex;gap:10px;padding:6px 0;font-size:9.5pt;align-items:flex-start}
.check .tick{flex:0 0 15px;height:15px;margin-top:2px;border-radius:4px;background:var(--yellow);color:var(--navy);font-size:9pt;font-weight:900;display:flex;align-items:center;justify-content:center;line-height:1}

/* ---- page footer ---- */
.foot{margin-top:20px;padding-top:11px;border-top:1px solid var(--line);display:flex;justify-content:space-between;font-size:8pt;color:var(--blue)}

/* ---- numbered steps ---- */
.values{margin-top:4px}
.value{display:flex;gap:14px;padding:11px 0;border-bottom:1px solid var(--line)}
.value:last-child{border-bottom:0}
.value .n{flex:0 0 30px;height:30px;border-radius:50%;background:var(--yellow);color:var(--navy);font-weight:800;font-size:11pt;display:flex;align-items:center;justify-content:center}
.value h3{margin-bottom:2px}
.value p{margin:0;font-size:9.5pt}

/* ---- faq ---- */
.faq{break-inside:avoid;margin-bottom:14px}
.faq h3{color:var(--navy);font-size:11pt}
.faq p{font-size:9.5pt;margin:0}
.faq-col{column-count:2;column-gap:26px}
.faq-col .faq{break-inside:avoid;margin-bottom:13px}

/* ---- closing CTA ---- */
.cta{background:var(--navy);color:#fff;border-radius:14px;padding:26px 28px;margin-top:auto}
.cta h2{color:#fff;font-size:17pt;margin-bottom:8px}
.cta p{color:var(--sky);font-size:10pt;margin-bottom:16px}
.cta .btn{display:inline-block;background:var(--yellow);color:var(--navy);font-weight:800;font-size:11pt;padding:11px 26px;border-radius:8px;text-decoration:none}
.cta .who{margin-top:16px;font-size:9.5pt;color:#fff;line-height:1.7}
.cta .who b{color:var(--yellow)}

/* ---- contents ---- */
.toc{margin-top:6px}
.toc-row{display:flex;align-items:baseline;gap:14px;padding:13px 0;border-bottom:1px solid var(--line)}
.toc-row:last-child{border-bottom:0}
.toc-n{flex:0 0 34px;font-size:9pt;font-weight:800;color:var(--yellow-dark);letter-spacing:.08em}
.toc-t{flex:1}
.toc-t b{display:block;font-size:12pt;color:var(--navy);font-weight:800;letter-spacing:-.01em}
.toc-t span{font-size:9.5pt;color:var(--body)}
.toc-p{flex:0 0 34px;text-align:right;font-size:11pt;font-weight:800;color:var(--royal)}
.toc-row--link{text-decoration:none;color:inherit;cursor:pointer;transition:opacity .12s}
.toc-row--link:hover{opacity:.62}

/* ---- glance band ---- */
.at-a-glance{background:var(--navy);border-radius:12px;padding:20px 22px;margin-top:auto;color:#fff}
.at-a-glance .eyebrow{color:var(--sky)}
.glance-grid{display:flex;gap:26px;margin-top:10px}
.glance-grid>div{flex:1;min-width:0}
.glance-grid .v{font-size:15pt;font-weight:800;color:var(--yellow);line-height:1.15}
.glance-grid .l{font-size:8.5pt;color:var(--sky);margin-top:2px}

/* ---- compact pricing table --------------------------------------------
   Tighter header and total bar so more options fit on a sheet. Every
   proposal keeps its own copy of the theme CSS, which loads after this and
   still carries the roomier .invest rules, so these selectors are one step
   more specific to win in proposals made before the change too. */
.invest table thead th{padding:6px 16px}
.invest table tbody td{padding:8px 16px}
.invest table .note{margin-top:1px}
.invest table tr.total td{padding:6px 16px;font-size:11pt;line-height:1.25;vertical-align:middle}
.invest table tr.total td.num{font-size:13pt}
`;
